import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffKpiProgressCard from "../components/staff/StaffKpiProgressCard";
import ConfirmModal from "../components/modals/ConfirmModal";
import { useAuth } from "../context/AuthContext";
import {
  getTechnicianAccount,
  getMyWeeklyHours,
  saveMyWorkingHours,
  setupTechnician,
} from "../api/technicians";
import {
  changeMyPassword,
  getMyKpiProgress,
  getMyProfile,
  removeMyProfilePhoto,
  updateMyProfile,
  uploadMyProfilePhoto,
} from "../api/profile";
import { getTechnicianLandingPath } from "../utils/technicianRouting";
import TimeSelect from "../components/TimeSelect";
import ImageUploadField from "../components/ImageUploadField";
import { formatNANP, isValidNANP, toNANPDigits10 } from "../utils/phone";

const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const DEFAULT_WORKING_HOURS = WEEK_DAYS.map((_, index) => ({
  day_of_week: index,
  start_time: index < 5 ? "09:00" : "10:00",
  end_time: index < 5 ? "17:00" : "15:00",
  is_working: index < 5,
}));

const DEFAULT_ACCOUNT_STATE = {
  staff_id: null,
  salon_display_name: "",
  phone: "",
  email: "",
  username: "",
  photo_url: "",
};

const DEFAULT_PROFILE_META = {
  full_name: "",
  first_name: "",
  last_name: "",
  current_salon_id: null,
  current_salon_name: "",
  current_business_id: null,
  current_business_name: "",
  linked_salon_ids: [],
  linked_business_ids: [],
};

const normalizeIdList = (value) => {
  const entries = Array.isArray(value) ? value : [];
  return [...new Set(entries.map((entry) => Number(entry)).filter(Number.isFinite))].sort(
    (a, b) => a - b
  );
};

const splitFullName = (fullName) => {
  const value = String(fullName || "").trim();
  if (!value) {
    return { first_name: "", last_name: "" };
  }
  const parts = value.split(/\s+/);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" "),
  };
};

const joinFullName = (firstName, lastName) =>
  [String(firstName || "").trim(), String(lastName || "").trim()]
    .filter(Boolean)
    .join(" ");

const normalizeProfileMeta = (payload = {}) => {
  const nameParts = splitFullName(payload?.full_name || "");
  return {
    full_name: payload?.full_name || "",
    first_name: nameParts.first_name,
    last_name: nameParts.last_name,
    current_salon_id: Number(payload?.current_salon_id || 0) || null,
    current_salon_name: payload?.current_salon_name || "",
    current_business_id: Number(payload?.current_business_id || 0) || null,
    current_business_name: payload?.current_business_name || "",
    linked_salon_ids: normalizeIdList(payload?.linked_salon_ids),
    linked_business_ids: normalizeIdList(payload?.linked_business_ids),
  };
};

const messageForError = (message) => {
  switch (message) {
    case "CURRENT_SALON_PROFILE_REQUIRED":
      return "No current salon profile is linked to this login yet.";
    case "FULL_NAME_REQUIRED":
      return "Full name is required.";
    case "USERNAME_INVALID":
      return "Username can only use letters, numbers, underscores, or hyphens.";
    case "USERNAME_TAKEN":
      return "That username is already in use.";
    case "SALON_DISPLAY_NAME_REQUIRED":
      return "Display name is required for the current salon profile.";
    case "SALON_CONTEXT_REQUIRED":
      return "Select a salon before editing salon-specific profile details.";
    case "INVALID_CURRENT_PASSWORD":
      return "Current password is incorrect.";
    default:
      return message;
  }
};

const badgeClass =
  "inline-flex items-center rounded-full border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700";

export default function TechnicianSetup() {
  const navigate = useNavigate();
  const {
    token,
    user,
    technicianStatus,
    refreshTechnicianOnboarding,
    logout,
    updateCurrentUser,
  } = useAuth();
  const [workingHours, setWorkingHours] = useState(
    () => DEFAULT_WORKING_HOURS.map((entry) => ({ ...entry }))
  );
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loadingHours, setLoadingHours] = useState(false);
  const [account, setAccount] = useState(() => ({ ...DEFAULT_ACCOUNT_STATE }));
  const [profileMeta, setProfileMeta] = useState(() => ({ ...DEFAULT_PROFILE_META }));
  const [accountError, setAccountError] = useState("");
  const [accountSuccess, setAccountSuccess] = useState("");
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [kpiProgress, setKpiProgress] = useState(null);
  const [loadingKpiProgress, setLoadingKpiProgress] = useState(false);
  const [kpiProgressError, setKpiProgressError] = useState("");

  const normalizeWeeklyHours = useCallback((source = []) => {
    const entries = Array.isArray(source) ? source : [];
    const byDay = {};
    entries.forEach((entry) => {
      if (!entry || typeof entry.day_of_week !== "number") return;
      byDay[entry.day_of_week] = entry;
    });
    return WEEK_DAYS.map((_, index) => {
      const matched = byDay[index];
      const defaultWorking = index < 5;
      const start = matched?.start_time ?? (defaultWorking ? "09:00" : "10:00");
      const end = matched?.end_time ?? (defaultWorking ? "17:00" : "15:00");
      const isWorking =
        typeof matched?.is_working === "boolean"
          ? matched.is_working
          : defaultWorking;
      return {
        day_of_week: index,
        start_time: start,
        end_time: end,
        is_working: isWorking,
      };
    });
  }, []);

  const loadWeeklyHours = useCallback(async () => {
    if (!token) return;
    setLoadingHours(true);
    setError("");
    try {
      const data = await getMyWeeklyHours(token);
      setWorkingHours(normalizeWeeklyHours(data));
    } catch (err) {
      console.error("Failed to load weekly working hours", err);
      setError(err?.message || "Unable to load working hours.");
    } finally {
      setLoadingHours(false);
    }
  }, [normalizeWeeklyHours, token]);

  const loadAccount = useCallback(async () => {
    if (!token) return;
    setLoadingAccount(true);
    setLoadingKpiProgress(true);
    setAccountError("");
    try {
      const [accountResult, profileResult, kpiResult] = await Promise.allSettled([
        getTechnicianAccount(token),
        getMyProfile(token),
        getMyKpiProgress(token),
      ]);
      if (accountResult.status !== "fulfilled") {
        throw accountResult.reason;
      }
      if (profileResult.status !== "fulfilled") {
        throw profileResult.reason;
      }
      const data = accountResult.value;
      const normalizedPhone = toNANPDigits10(data?.phone || "");
      setAccount({
        staff_id: Number(data?.staff_id || 0) || null,
        salon_display_name: data?.salon_display_name || "",
        phone: normalizedPhone || "",
        email: data?.email || "",
        username: data?.username || "",
        photo_url: data?.photo_url || "",
      });
      setProfileMeta(normalizeProfileMeta(profileResult.value));
      if (kpiResult.status === "fulfilled") {
        setKpiProgress(kpiResult.value);
        setKpiProgressError("");
      } else {
        setKpiProgress(null);
        setKpiProgressError(kpiResult.reason?.message || "Unable to load KPI progress.");
      }
    } catch (err) {
      console.error("Failed to load technician account", err);
      setAccountError(
        messageForError(err?.message || "Unable to load account settings.")
      );
    } finally {
      setLoadingAccount(false);
      setLoadingKpiProgress(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user?.salon_id) {
      navigate("/login", { replace: true });
    }
  }, [user?.salon_id, navigate]);

  useEffect(() => {
    loadWeeklyHours();
  }, [loadWeeklyHours, user?.salon_id]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount, user?.salon_id]);

  const handleHourChange = (index, field, value) => {
    setSuccessMessage("");
    setWorkingHours((prev) =>
      prev.map((hour, idx) =>
        idx === index ? { ...hour, [field]: value } : hour
      )
    );
  };

  const toggleWorkingDay = (index, checked) => {
    setSuccessMessage("");
    setWorkingHours((prev) =>
      prev.map((hour, idx) =>
        idx === index ? { ...hour, is_working: checked } : hour
      )
    );
  };

  const handleAccountFieldChange = (field, value) => {
    setAccountSuccess("");
    setAccountError("");
    if (field === "phone" && phoneError) {
      setPhoneError("");
    }
    setAccount((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileFieldChange = (field, value) => {
    setAccountSuccess("");
    setAccountError("");
    setProfileMeta((prev) => ({ ...prev, [field]: value }));
  };

  const applyProfileResponse = useCallback(
    (data) => {
      const normalizedPhone = toNANPDigits10(data?.phone || "");
      setAccount({
        staff_id: Number(data?.staff_id || 0) || null,
        salon_display_name: data?.salon_display_name || "",
        phone: normalizedPhone || "",
        email: data?.email || "",
        username: data?.username || "",
        photo_url: data?.photo_url || "",
      });
      setProfileMeta(normalizeProfileMeta(data));
      updateCurrentUser({
        full_name: data?.full_name || "",
        email: data?.email || "",
        photo_url: data?.photo_url || "",
      });
    },
    [updateCurrentUser]
  );

  const handleUploadPhoto = async (file) => {
    if (!file || !token) return "";
    const result = await uploadMyProfilePhoto(file, token);
    applyProfileResponse(result);
    return result?.photo_url || "";
  };

  const handleRemovePhoto = async () => {
    if (!token) return "";
    const result = await removeMyProfilePhoto(token);
    applyProfileResponse(result);
    return result?.photo_url || "";
  };

  const handleSaveAccount = async (event) => {
    event?.preventDefault?.();
    if (!token) return;
    const phoneDigits = account.phone ? toNANPDigits10(account.phone) : "";
    if (phoneDigits && !isValidNANP(phoneDigits)) {
      setPhoneError("Phone must be a 10-digit North American number.");
      return;
    }

    setSavingAccount(true);
    setAccountError("");
    setAccountSuccess("");
    try {
      const fullName = joinFullName(
        profileMeta.first_name,
        profileMeta.last_name
      );
      const payload = {
        full_name: fullName,
        salon_display_name: account.salon_display_name.trim(),
        phone: phoneDigits || null,
        email: account.email.trim(),
        username: account.username.trim() || null,
      };
      const data = await updateMyProfile(payload, token);
      applyProfileResponse(data);
      setAccountSuccess("Profile settings saved.");
    } catch (err) {
      const message =
        messageForError(err?.message) ||
        "Unable to save profile settings.";
      setAccountError(message);
    } finally {
      setSavingAccount(false);
    }
  };

  const handlePasswordSubmit = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    if (!newPassword || newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    if (!token) {
      setPasswordError("You are no longer signed in.");
      return;
    }

    setChangingPassword(true);
    try {
      await changeMyPassword(
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        token
      );
      setPasswordSuccess("Password updated. You will need to sign in again.");
      setShowPasswordModal(true);
    } catch (err) {
      setPasswordError(
        messageForError(err?.message || "Could not change password.")
      );
    } finally {
      setChangingPassword(false);
    }
  };

  const isOnboardingIncomplete = technicianStatus?.setup_complete !== true;

  // Manual test checklist:
  // 1. Login as a technician user.
  // 2. Open /technician/setup and adjust Monday's start time.
  // 3. Click Save and confirm the success message appears.
  // 4. Refresh the page and verify the updated time persists.
  // 5. Confirm PUT /technicians/me/hours returns 200 with the updated payload.
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) return;
    setIsSaving(true);
    setError("");
    setSuccessMessage("");
    const payload = { weekly_hours: workingHours };
    try {
      if (isOnboardingIncomplete) {
        await setupTechnician(payload, token);
        const onboardingData = await refreshTechnicianOnboarding();
        const hasSelectedSalon = Boolean(user?.salon_id);
        const landingPath = getTechnicianLandingPath({
          hasSelectedSalon,
          profileComplete:
            onboardingData?.profile_complete ?? technicianStatus?.profile_complete ?? true,
          staffSetupComplete:
            onboardingData?.staff_setup_complete ??
            technicianStatus?.staff_setup_complete ??
            true,
        });
        navigate(landingPath, { replace: true });
        return;
      }
      await saveMyWorkingHours(payload, token);
      await loadWeeklyHours();
      setSuccessMessage("Working hours saved.");
    } catch (err) {
      console.error("Failed to save working hours", err);
      const message =
        err?.response?.data?.detail || err?.message || "Unable to save working hours.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const workingHoursRows = useMemo(
    () =>
      workingHours.map((entry, index) => (
        <div
          key={entry.day_of_week}
          className="flex flex-wrap gap-3 items-center border-b border-slate-100 py-2"
        >
          <label className="flex items-center gap-2 text-sm font-semibold w-28">
            <input
              type="checkbox"
              checked={entry.is_working}
              onChange={(event) =>
                toggleWorkingDay(entry.day_of_week, event.target.checked)
              }
            />
            {WEEK_DAYS[index]}
          </label>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">Start</span>
            <TimeSelect
              value={entry.start_time}
              onChange={(next) => handleHourChange(index, "start_time", next)}
              disabled={!entry.is_working}
              className="w-full max-w-xs"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">End</span>
            <TimeSelect
              value={entry.end_time}
              onChange={(next) => handleHourChange(index, "end_time", next)}
              disabled={!entry.is_working}
              className="w-full max-w-xs"
            />
          </div>
        </div>
      )),
    [workingHours]
  );

  const linkedSalonIds = useMemo(
    () => normalizeIdList(profileMeta.linked_salon_ids),
    [profileMeta.linked_salon_ids]
  );
  const linkedBusinessIds = useMemo(
    () => normalizeIdList(profileMeta.linked_business_ids),
    [profileMeta.linked_business_ids]
  );

  const handlePasswordAcknowledge = async () => {
    setShowPasswordModal(false);
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-5xl mx-auto px-4 space-y-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">
            My Profile
          </h1>
          <p className="text-sm text-slate-500">
            Update your account details, current-salon profile, password, and
            working hours from one place.
          </p>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                Manage
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                Technician My Profile
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-slate-500">
                This page combines your login info, linked IDs, salon display
                profile, password, and schedule availability.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <div>
                <span className="font-semibold text-slate-900">Current Salon:</span>{" "}
                {profileMeta.current_salon_name || user?.salon_name || "None selected"}
                {profileMeta.current_salon_id || user?.salon_id
                  ? ` (ID ${profileMeta.current_salon_id || user?.salon_id})`
                  : ""}
              </div>
              <div className="mt-1">
                <span className="font-semibold text-slate-900">Current Business:</span>{" "}
                {profileMeta.current_business_name || "Not available"}
                {profileMeta.current_business_id
                  ? ` (ID ${profileMeta.current_business_id})`
                  : ""}
              </div>
            </div>
          </div>
        </div>

        {(linkedSalonIds.length > 0 || linkedBusinessIds.length > 0) && (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="text-lg font-semibold text-slate-900">Linked IDs</div>
            <p className="mt-1 text-sm text-slate-500">
              These are the salon and business records linked to this technician login.
            </p>
            <div className="mt-4 grid gap-5 md:grid-cols-2">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">
                  Salon IDs
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {linkedSalonIds.length > 0 ? (
                    linkedSalonIds.map((id) => (
                      <span key={`technician-linked-salon-${id}`} className={badgeClass}>
                        {id}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-slate-400">None</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">
                  Business IDs
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {linkedBusinessIds.length > 0 ? (
                    linkedBusinessIds.map((id) => (
                      <span
                        key={`technician-linked-business-${id}`}
                        className={badgeClass}
                      >
                        {id}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-slate-400">None</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <StaffKpiProgressCard
          title="Targeted KPI"
          subtitle="Review your current month progress against the KPI targets assigned to your technician profile."
          progress={kpiProgress}
          loading={loadingKpiProgress}
          error={kpiProgressError}
        />

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-slate-200 rounded-lg p-6 space-y-6 shadow-sm"
        >
          {error && (
            <div className="rounded bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}
          {successMessage && (
            <div className="rounded bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
              {successMessage}
            </div>
          )}
          {loadingHours && (
            <div className="text-sm text-slate-500">
              Loading saved working hours...
            </div>
          )}
          {loadingAccount && (
            <div className="text-sm text-slate-500">
              Loading profile settings...
            </div>
          )}
          {passwordError && (
            <div className="rounded bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">
              {passwordError}
            </div>
          )}
          {passwordSuccess && !showPasswordModal && (
            <div className="rounded bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
              {passwordSuccess}
            </div>
          )}

          <section className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Profile settings</h2>
              <p className="text-sm text-slate-500">
                Update your login details and the current salon profile used in schedule and navbar views.
              </p>
            </div>

            {accountError && (
              <div className="rounded bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">
                {accountError}
              </div>
            )}
            {accountSuccess && (
              <div className="rounded bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                {accountSuccess}
              </div>
            )}

            <ImageUploadField
              label="Profile Picture"
              helper="Upload a clear headshot used across schedule and navbar."
              recommended="Square image recommended"
              value={account.photo_url}
              onChange={(next) => handleAccountFieldChange("photo_url", next || "")}
              onUpload={handleUploadPhoto}
              onRemove={handleRemovePhoto}
              allowUrlPaste={false}
              maxSizeMb={2}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  First Name
                </label>
                <input
                  type="text"
                  value={profileMeta.first_name}
                  onChange={(event) =>
                    handleProfileFieldChange("first_name", event.target.value)
                  }
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  Last Name
                </label>
                <input
                  type="text"
                  value={profileMeta.last_name}
                  onChange={(event) =>
                    handleProfileFieldChange("last_name", event.target.value)
                  }
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder="Last name"
                />
              </div>
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  Salon Display Name
                </label>
                <input
                  type="text"
                  value={account.salon_display_name}
                  onChange={(event) =>
                    handleAccountFieldChange("salon_display_name", event.target.value)
                  }
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder="Name shown in salon schedule"
                />
              </div>
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={isValidNANP(account.phone) ? formatNANP(account.phone) : account.phone}
                  onChange={(event) =>
                    handleAccountFieldChange("phone", toNANPDigits10(event.target.value))
                  }
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder="(555) 123-4567"
                />
                {phoneError && <p className="mt-1 text-xs text-rose-600">{phoneError}</p>}
              </div>
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  Email
                </label>
                <input
                  type="email"
                  value={account.email}
                  onChange={(event) => handleAccountFieldChange("email", event.target.value)}
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  Username
                </label>
                <input
                  type="text"
                  value={account.username}
                  onChange={(event) => handleAccountFieldChange("username", event.target.value)}
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder="username"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSaveAccount}
                disabled={savingAccount}
                className="rounded bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70"
              >
                {savingAccount ? "Saving..." : "Save profile settings"}
              </button>
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Change Password</h2>
              <p className="text-sm text-slate-500">
                Update your login password here. It will apply everywhere this technician
                account is linked.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                />
              </div>
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-4 text-base shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handlePasswordSubmit}
                disabled={changingPassword}
                className="rounded bg-rose-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-70"
              >
                {changingPassword ? "Updating..." : "Update Password"}
              </button>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-900">
              Working hours
            </h2>
            <p className="text-sm text-slate-500">
              Toggle days you work and select your usual start/end times.
            </p>
            <div className="rounded border border-slate-100 bg-slate-50">
              {workingHoursRows}
            </div>
          </section>

          <section className="space-y-2">
            <h2 className="text-lg font-semibold text-slate-900">Services</h2>
            <p className="text-sm text-slate-500">
              Managers control the services you can perform per salon. Saving your
              working hours ensures your name appears in the schedule when you&apos;re
              available.
            </p>
          </section>

          <button
            type="submit"
            disabled={isSaving}
            className="w-full rounded bg-rose-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-70"
          >
            {isSaving ? "Saving..." : "Save working hours"}
          </button>
        </form>
      </div>
      <ConfirmModal
        isOpen={showPasswordModal}
        title="Password Updated"
        message="Your password has been updated successfully. Please log in again."
        confirmText="OK"
        hideCancel
        showClose={false}
        onConfirm={handlePasswordAcknowledge}
        onClose={handlePasswordAcknowledge}
      />
    </div>
  );
}
